"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import styles from "./productsShowcase.module.css";

export type ShowcaseProduct = {
  id: string;
  title: string;
  tagline: string;
  description: string;
  category: string;
  image: string;
  imageAlt: string;
  href: string;
  features: string[];
  tone: "light" | "blush" | "gradient" | "sky";
  cover?: boolean;
};

const ease = [0.22, 1, 0.36, 1] as const;

function ProductRow({
  product,
  index,
}: {
  product: ShowcaseProduct;
  index: number;
}) {
  const reduceMotion = useReducedMotion();
  const reverse = index % 2 === 1;
  const toneClass =
    product.tone === "gradient"
      ? styles.toneGradient
      : product.tone === "blush"
        ? styles.toneBlush
        : product.tone === "sky"
          ? styles.toneSky
          : styles.toneLight;

  const container = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.1,
        delayChildren: reduceMotion ? 0 : 0.05,
      },
    },
  };

  const fadeUp = {
    hidden: reduceMotion
      ? { opacity: 1, y: 0 }
      : { opacity: 0, y: 36 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease },
    },
  };

  const mediaIn = {
    hidden: reduceMotion
      ? { opacity: 1, x: 0, scale: 1 }
      : {
          opacity: 0,
          x: reverse ? 56 : -56,
          scale: 0.94,
        },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: 0.75, ease },
    },
  };

  return (
    <motion.article
      id={product.id}
      className={`${styles.row} ${toneClass} ${reverse ? styles.reverse : ""}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.28 }}
      variants={container}
    >
      <div className={styles.rowInner}>
        <motion.div className={styles.mediaCol} variants={mediaIn}>
          <div className={styles.mediaFrame}>
            <span className={styles.mediaGlow} aria-hidden />
            <span className={styles.index} aria-hidden>
              {String(index + 1).padStart(2, "0")}
            </span>
            <Image
              src={product.image}
              alt={product.imageAlt}
              width={560}
              height={560}
              className={`${styles.mediaImage} ${
                product.cover ? styles.mediaCover : ""
              }`}
            />
          </div>
        </motion.div>

        <motion.div className={styles.copyCol} variants={container}>
          <motion.span className={styles.badge} variants={fadeUp}>
            {product.category}
          </motion.span>
          <motion.h2 className={styles.title} variants={fadeUp}>
            {product.title}
          </motion.h2>
          <motion.p className={styles.tagline} variants={fadeUp}>
            {product.tagline}
          </motion.p>
          <motion.p className={styles.description} variants={fadeUp}>
            {product.description}
          </motion.p>
          <motion.ul className={styles.features} variants={fadeUp}>
            {product.features.map((feature) => (
              <li key={feature} className={styles.feature}>
                {feature}
              </li>
            ))}
          </motion.ul>
          <motion.div variants={fadeUp}>
            <Link href={product.href} className={styles.cta}>
              Discover More
              <svg
                className={styles.ctaIcon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </motion.article>
  );
}

type ProductsShowcaseProps = {
  products: ShowcaseProduct[];
};

export default function ProductsShowcase({ products }: ProductsShowcaseProps) {
  const reduceMotion = useReducedMotion();

  return (
    <section className={styles.section} aria-labelledby="products-list-heading">
      <motion.div
        className={styles.intro}
        initial={reduceMotion ? false : { opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 0.55, ease }}
      >
        <p className={styles.introEyebrow}>Explore the suite</p>
        <h2 id="products-list-heading" className={styles.introTitle}>
          Products designed for every stage of growth
        </h2>
        <p className={styles.introText}>
          From communication apps to sensory tools, each product is built to
          support therapy, learning, and everyday progress.
        </p>
      </motion.div>

      <div className={styles.list}>
        {products.map((product, index) => (
          <ProductRow key={product.id} product={product} index={index} />
        ))}
      </div>
    </section>
  );
}
